import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, MapPin, Truck, Store, Banknote, Smartphone, CreditCard, HandCoins, Wallet, Star } from "lucide-react";
import { API, fmt } from "@/lib/api";
import axios from "axios";

const METHOD_META = {
  cashapp: { label: "CashApp", icon: Banknote },
  applepay: { label: "Apple Pay", icon: Smartphone },
  venmo: { label: "Venmo", icon: Wallet },
  stripe: { label: "Card", icon: CreditCard },
  in_person: { label: "Pay in Person", icon: HandCoins },
};

export default function OrderConfirmed() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/orders/${orderId}`)
      .then((res) => setOrder(res.data))
      .catch(() => setError(true));
    axios
      .get(`${API}/content`)
      .then((res) => setContent(res.data))
      .catch(() => {});
  }, [orderId]);

  const payment = content?.payment || {};
  const footer = content?.footer || {};
  const address = [footer.address1, footer.address2].filter(Boolean).join(", ");

  const renderInstructions = () => {
    if (!order) return null;
    if (order.payment_method === "cashapp") {
      const tag = payment.cashtag || "$YourCashTag";
      const clean = tag.replace("$", "");
      return (
        <div className="bg-parchdeep/70 rounded-xl p-5" data-testid="payment-instructions">
          <p className="font-semibold text-sm text-ink">
            Send <span className="font-serif font-black text-terra">{fmt(order.total)}</span> to{" "}
            <span className="font-mono font-bold">{tag}</span> on CashApp
          </p>
          <p className="text-xs text-ink/60 mt-1.5 leading-relaxed">
            Put your batch code <span className="font-mono font-bold">{order.id}</span> in the payment
            note so we can match it to your jars.
          </p>
          <a
            href={`https://cash.app/$${clean}/${order.total}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-2 bg-[#00D632] hover:bg-[#00b92b] text-white font-bold rounded-full py-3 text-sm transition-colors"
            data-testid="cashapp-pay-link"
          >
            <Banknote className="w-4 h-4" /> Open CashApp to Pay {fmt(order.total)}
          </a>
        </div>
      );
    }
    if (order.payment_method === "applepay") {
      return (
        <div className="bg-parchdeep/70 rounded-xl p-5" data-testid="payment-instructions">
          <p className="font-semibold text-sm text-ink">
            Send <span className="font-serif font-black text-terra">{fmt(order.total)}</span> via Apple
            Pay / Apple Cash to <span className="font-mono font-bold">{payment.apple_pay || "us"}</span>
          </p>
          <p className="text-xs text-ink/60 mt-1.5 leading-relaxed">
            Include your batch code <span className="font-mono font-bold">{order.id}</span> in the
            message so we can match it to your jars.
          </p>
        </div>
      );
    }
    if (order.payment_method === "venmo") {
      const user = (payment.venmo || "PickleMeOK405").replace(/^@/, "");
      return (
        <div className="bg-parchdeep/70 rounded-xl p-5" data-testid="payment-instructions">
          <p className="font-semibold text-sm text-ink">
            Send <span className="font-serif font-black text-terra">{fmt(order.total)}</span> on Venmo to{" "}
            <span className="font-mono font-bold">@{user}</span>
          </p>
          <p className="text-xs text-ink/60 mt-1.5 leading-relaxed">
            Put your batch code <span className="font-mono font-bold">{order.id}</span> in the payment
            note so we can match it to your jars.
          </p>
          <a
            href={`https://venmo.com/${user}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-2 bg-[#008CFF] hover:bg-[#0074d4] text-white font-bold rounded-full py-3 text-sm transition-colors"
            data-testid="venmo-pay-link"
          >
            <Wallet className="w-4 h-4" /> Open Venmo to Pay {fmt(order.total)}
          </a>
        </div>
      );
    }
    if (order.payment_method === "stripe") {
      // legacy card orders (Stripe removed) — static display only
      return (
        <div className="bg-parchdeep/70 rounded-xl p-5 text-center" data-testid="payment-instructions">
          <CreditCard className="w-5 h-5 mx-auto mb-1.5 text-dill" />
          <p className="font-semibold text-sm text-ink">
            {order.status === "paid" || order.status === "fulfilled"
              ? "Card payment received — thank you!"
              : "This order was placed for card payment — we'll confirm it with you directly."}
          </p>
        </div>
      );
    }
    return (
      <div className="bg-parchdeep/70 rounded-xl p-5" data-testid="payment-instructions">
        <p className="font-semibold text-sm text-ink">
          Pay <span className="font-serif font-black text-terra">{fmt(order.total)}</span> when you pick up
        </p>
        <p className="text-xs text-ink/60 mt-1.5 leading-relaxed">
          {payment.in_person_note || "Cash or card accepted at the farmstand."}
          {address ? ` Find us at ${address}.` : ""} We'll hold your jars for 48 hours.
        </p>
      </div>
    );
  };

  const meta = order ? METHOD_META[order.payment_method] || METHOD_META.in_person : null;

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center px-4 py-16" data-testid="order-confirmed-page">
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-lg"
      >
        {!order && !error && (
          <div className="text-center" data-testid="order-loading">
            <Loader2 className="w-10 h-10 animate-spin text-dill mx-auto mb-4" />
            <p className="font-serif text-2xl text-dill">Packing your ticket…</p>
          </div>
        )}

        {error && (
          <div className="text-center bg-white rounded-2xl border border-parchborder p-10" data-testid="order-error">
            <p className="font-serif text-2xl text-dill">We couldn't find that order.</p>
            <Link to="/" className="inline-block mt-6 text-terra font-semibold text-sm hover:underline" data-testid="error-home-link">
              Back to the farmstand
            </Link>
          </div>
        )}

        {order && (
          <div className="bg-white rounded-2xl border border-parchborder shadow-xl overflow-hidden" data-testid="order-confirmation-ticket">
            <div className="bg-dill text-parchment px-8 py-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.2 }}
              >
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-gold" />
              </motion.div>
              <h1 className="font-serif font-black text-3xl">Order Placed!</h1>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mt-2" data-testid="order-batch-code">
                Batch Reservation #{order.id}
              </p>
            </div>

            <div className="px-8 py-6 space-y-5">
              <div className="flex items-center gap-3">
                {order.fulfillment === "pickup" ? (
                  <Store className="w-5 h-5 text-terra shrink-0" />
                ) : (
                  <Truck className="w-5 h-5 text-terra shrink-0" />
                )}
                <div>
                  <p className="font-semibold text-sm text-ink" data-testid="order-fulfillment-method">
                    {order.fulfillment === "pickup" ? "Local Pickup" : "Delivery (OKC Metro)"}
                  </p>
                  <p className="text-xs text-ink/55">
                    {order.fulfillment === "pickup"
                      ? address || "Wellston, OK — we'll message you when it's ready"
                      : `Heading to: ${[order.delivery_address, order.delivery_city].filter(Boolean).join(", ") || "your address"}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {meta && <meta.icon className="w-5 h-5 text-terra shrink-0" />}
                <p className="font-semibold text-sm text-ink" data-testid="order-payment-method">
                  {meta?.label}
                </p>
              </div>

              {renderInstructions()}

              <div className="space-y-2 border-t border-dashed border-parchborder pt-4">
                {order.items.map((item) => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span className="text-ink/75">
                      {item.quantity} × {item.name}
                    </span>
                    <span className="font-medium text-ink">{fmt(item.unit_price * item.quantity)}</span>
                  </div>
                ))}
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-dill font-semibold">Promo {order.promo_code}</span>
                    <span className="font-medium text-dill" data-testid="order-discount">−{fmt(order.discount)}</span>
                  </div>
                )}
                {order.delivery_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-ink/75">Delivery</span>
                    <span className="font-medium text-ink">{fmt(order.delivery_fee)}</span>
                  </div>
                )}
                {order.tax_amount > 0 && !order.tax_inclusive && (
                  <div className="flex justify-between text-sm">
                    <span className="text-ink/75">
                      Sales tax{order.city ? ` (${order.city}${order.tax_rate ? ` ${order.tax_rate}%` : ""})` : ""}
                    </span>
                    <span className="font-medium text-ink">{fmt(order.tax_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-serif font-bold text-lg text-ink border-t border-parchborder pt-3 mt-3">
                  <span>Total</span>
                  <span data-testid="order-total-paid">{fmt(order.total)}</span>
                </div>
                {order.tax_inclusive && (
                  <p className="text-right text-xs text-ink/50 italic" data-testid="order-tax-note">
                    All prices include sales tax{order.tax_amount > 0 ? ` — ${fmt(order.tax_amount)} included${order.city ? ` (${order.city})` : ""}` : ""}
                  </p>
                )}
              </div>

              <p className="font-mono text-[10px] uppercase tracking-widest text-ink/40">
                For: {order.customer_name}
                {[order.customer_phone, order.customer_email].filter(Boolean).length > 0 &&
                  ` • ${[order.customer_phone, order.customer_email].filter(Boolean).join(" • ")}`}
              </p>

              {order.fulfillment === "pickup" && address && (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 border-2 border-dill/20 hover:border-terra hover:text-terra text-dill font-semibold rounded-full py-3 text-sm transition-colors"
                  data-testid="pickup-directions-link"
                >
                  <MapPin className="w-4 h-4" /> Get Pickup Directions
                </a>
              )}

              <Link
                to="/"
                className="flex items-center justify-center bg-dill hover:bg-terra text-parchment font-semibold rounded-full py-3 text-sm transition-colors"
                data-testid="success-home-button"
              >
                Back to the Pantry
              </Link>
            </div>
          </div>
        )}

        {order && (
          <div
            className="mt-6 bg-white rounded-2xl border border-gold/50 shadow-xl p-6 text-center"
            data-testid="review-prompt-card"
          >
            <div className="flex items-center justify-center gap-1 mb-2.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className="w-4 h-4 fill-gold text-gold" />
              ))}
            </div>
            <p className="font-serif font-bold text-lg text-dill">Loved your jar?</p>
            <p className="text-xs text-ink/60 mt-1 mb-4 leading-relaxed max-w-xs mx-auto">
              Tell the neighborhood how the crunch landed — your words keep this little farmstand humming.
            </p>
            <Link
              to={`/?review=1&name=${encodeURIComponent(order.customer_name || "")}`}
              className="inline-flex items-center justify-center gap-2 bg-terra hover:bg-terra-hover text-parchment font-bold rounded-full px-6 py-2.5 text-sm transition-colors"
              data-testid="review-prompt-button"
            >
              <Star className="w-4 h-4" /> Leave a Review
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
